import { Card, CardContent } from "@/shared/ui/card";

import type { GeoReport } from "../../../api/geoService";
import { CitationSources } from "../report/CitationSources";
import { PresenceChecklist } from "../report/PresenceChecklist";
import { SearchInsight } from "../report/SearchInsight";
import { Kpi, SecNo } from "./shared";

/** 09 品牌信任资产:三卡富字段(照实习生)+ 信源阵地体检收编。 */
export function TrustAssetsSec({ report }: { report: GeoReport }) {
  const trust = report.trust_assets;
  if (!trust?.available || !trust.assets?.length) return null;
  const checkOf: Record<string, string> = {
    hotline_400: "全网检索核验号码与品牌关联一致性",
    icp: "官网可达性 + ICP 备案号抓取核验",
    baike: "百科生态品牌词条检索",
  };
  const impactOf: Record<string, string> = {
    hotline_400: "提升 AI 对企业真实经营的判断",
    icp: "官网是 AI 引用的第一信源",
    baike: "采购类问题 AI 倾向引用有百科背书的品牌,权威信源加权直接受益",
  };
  return (
    <section id="geo-sec-trust" className="space-y-2">
      <SecNo
        no="09"
        title="品牌信任资产"
        desc="AI 判断「这家公司可信吗」的三块硬资产 · 计入总分 10 分"
      />
      <Card>
        <CardContent className="p-6">
          <div className="grid gap-3 md:grid-cols-3" data-testid="geo-trust-cards">
            {trust.assets.map((a) => (
              <div
                key={a.key}
                className="rounded-xl border bg-background px-3.5 py-3 text-2xs leading-relaxed"
              >
                <div className="mb-1.5 flex items-center gap-2">
                  <b className="text-[13px]">{a.label}</b>
                  <span
                    className={`ml-auto rounded-full px-2 py-0.5 text-2xs font-bold ${
                      a.verified
                        ? "bg-emerald-500/15 text-emerald-600"
                        : "bg-rose-500/12 text-rose-500"
                    }`}
                  >
                    {a.verified ? "已核验" : "未发现"}
                  </span>
                </div>
                <div className="grid grid-cols-[44px_1fr] gap-1.5 border-t border-dashed py-1">
                  <span className="text-2xs text-muted-foreground">检查项</span>
                  <span>{checkOf[a.key] ?? "联网检索核验"}</span>
                </div>
                <div className="grid grid-cols-[44px_1fr] gap-1.5 border-t border-dashed py-1">
                  <span className="text-2xs text-muted-foreground">发现</span>
                  <span>{a.verified ? a.value || "已核验到" : "未检索到"}</span>
                </div>
                <div className="grid grid-cols-[44px_1fr] gap-1.5 border-t border-dashed py-1">
                  <span className="text-2xs text-muted-foreground">影响</span>
                  <span>{impactOf[a.key] ?? ""}</span>
                </div>
                <div className="grid grid-cols-[44px_1fr] gap-1.5 border-t border-dashed py-1">
                  <span className="text-2xs text-muted-foreground">
                    {a.verified ? "证据" : "建议"}
                  </span>
                  {a.verified ? (
                    a.evidence?.url ? (
                      <a
                        className="truncate text-primary hover:underline"
                        href={a.evidence.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {a.evidence.title || a.evidence.domain || a.evidence.url}
                      </a>
                    ) : (
                      <span>{a.evidence?.domain ?? "—"}</span>
                    )
                  ) : (
                    <span>补齐该资产(内容计划已排入对应主题)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {trust.basis && <div className="mt-2 text-2xs text-muted-foreground">{trust.basis}</div>}
        </CardContent>
      </Card>
      {/* 旧「信源阵地体检」收编(A2 内容不丢) */}
      <PresenceChecklist report={report} />
    </section>
  );
}

/** 01 全网搜索概览:KPI + 来源域名 TOP + 样本蓝链;检索归因/引用源旧卡收编。 */
export function SearchOverviewSec({ report }: { report: GeoReport }) {
  const ov = report.search_overview;
  if (!ov?.available) return null;
  return (
    <section id="geo-sec-search" className="space-y-2">
      {/* ★段名保持「全网搜索概览」不改成「百度搜索概览」(2026-07-29 测试提过改名):
          这一段的检索源是智谱 web_search(search_std + search_pro_sogou=搜狗)+ 通义百炼原生检索,
          TOP 域名里 douyin/map.360.cn 也证明不是百度 SERP —— 叫「百度」是对客户的不实陈述。
          真缺陷是「点不动」:引用 URL 原本是 <span> 纯文本。现在问题跳百度搜索页、URL 跳原文。 */}
      <SecNo
        no="01"
        title="全网搜索概览"
        desc="联网检索引用池 · 检索源:联网检索(含搜狗等)· 点问题去百度看真实结果"
      />
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <Kpi value={ov.valid_domain_count ?? 0} label="有效引用域名" />
            <Kpi value={ov.citation_url_count ?? 0} label="引用链接总数" />
            <Kpi value={ov.valid_answers ?? 0} label="有效回答(实测)" />
            <Kpi value={ov.negative_hits ?? 0} label="负向命中" negative />
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-[280px_1fr]">
            <div>
              <h6 className="mb-1.5 text-xs font-bold">主要来源域名 TOP</h6>
              <table className="w-full text-xs">
                <tbody>
                  {(ov.source_distribution ?? []).slice(0, 8).map((s) => (
                    <tr key={s.domain} className="border-b last:border-b-0">
                      <td className="py-1.5">{s.domain}</td>
                      <td className="py-1.5 text-right font-mono tabular-nums">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div data-testid="geo-search-samples" className="min-w-0">
              <h6 className="mb-1.5 text-xs font-bold">检索回答样本(节选 · 实测)</h6>
              {(ov.samples ?? []).map((s) => (
                <div
                  key={`${s.platform}-${s.question}`}
                  className="min-w-0 border-b border-dashed py-2 text-xs leading-relaxed last:border-b-0"
                >
                  {/* 问题 → 百度搜索结果页(测试要「点击后跳转对应的百度页面」);
                      蓝色加粗本来就长得像链接,以前却点不动。 */}
                  <a
                    href={`https://www.baidu.com/s?wd=${encodeURIComponent(s.question)}`}
                    target="_blank"
                    rel="noreferrer"
                    title="在百度中查看这个问题的真实搜索结果"
                    className="block break-words font-semibold text-primary hover:underline"
                  >
                    {s.question} · {s.platform_label}
                  </a>
                  {s.citation_urls.length > 0 && (
                    /* 引用 URL 原本是 <span> 纯文本 —— 看着是链接却点不动。逐条成真链接。 */
                    /* 1006:长 URL 横向撑破卡片 → 允许任意处断行(grid 子项要 min-w-0 才收得住) */
                    <span className="block min-w-0 break-all text-2xs">
                      {s.citation_urls.map((u, i) => (
                        <span key={u}>
                          {i > 0 && <span className="text-muted-foreground"> · </span>}
                          <a
                            href={u}
                            target="_blank"
                            rel="noreferrer"
                            className="break-all text-emerald-600 hover:underline"
                          >
                            {u}
                          </a>
                        </span>
                      ))}
                    </span>
                  )}
                  <p className="mt-0.5 text-2xs text-muted-foreground">{s.answer_excerpt}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      {/* 旧「检索归因 SoC/引擎归因」「引用源聚合」收编(内容不丢) */}
      <SearchInsight report={report} />
      <CitationSources report={report} />
    </section>
  );
}
